import mqtt from "mqtt";
import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {v4 as uuidv4} from "uuid";

// AWS IoT Core configuration. The backend (cres-aws-t13) registers:
//   * Custom authorizer named "cres-t13" (terraform/.../iot/main.tf)
//   * Authorizer enforces username === "cres" (handlers/authorizer/rules/iot.py)
//   * IoT topic rule named "cres_web_t13" (terraform/.../iot/main.tf)
//   * IAM policy authorizes `topicfilter/cres_web_t13/v1/*` for subscribe and
//     `topic/cres_web_t13/v1/*` for receive (free-form topic filters allowed).
// Any drift here from those constants and IoT rejects the connection or
// silently denies the subscribe.
const MQTT_AUTHORIZER_NAME = "cres-t13";
const MQTT_USERNAME = "cres";
const TOPIC_PREFIX = "cres_web_t13/v1";

/**
 * Marker the test publisher (cres-aws-t13/scripts/mqtt/test-mqtt-echo.py)
 * tags messages with. Only payloads carrying this kind value light up the
 * NavBar indicator so day-to-day server-pushed traffic (notifications, sync
 * events, etc.) doesn't make the indicator pulse continuously and confuse
 * it with the test signal.
 */
export const MQTT_PROBE_KIND = "cres-mqtt-echo-probe";

export type MqttReceivedSummary = {
    /** Topic the most recent probe arrived on. null until first probe. */
    lastTopic: string | null;
    /** Decoded body of the most recent probe. */
    lastPayload: unknown;
    /** Wall-clock timestamp the most recent probe arrived. */
    lastReceivedAt: number | null;
    /** Running counter of probe messages received since connect. */
    probeCount: number;
    /** Running counter of ALL messages received since connect (for debug). */
    totalCount: number;
};

const INITIAL_RECEIVED: MqttReceivedSummary = {
    lastTopic: null,
    lastPayload: null,
    lastReceivedAt: null,
    probeCount: 0,
    totalCount: 0,
};

export type MqttClientHandle = {
    /** The underlying mqtt.js client (null until connect resolves). */
    client: any;
    /** Sticky summary of every message that's come in since connect. */
    received: MqttReceivedSummary;
};

export function useMqttClient(sessionContext: any): MqttClientHandle {

    const [client, setClient] = useState<any>(undefined);
    const [received, setReceived] = useState<MqttReceivedSummary>(INITIAL_RECEIVED);

    const location = useLocation();

    useEffect(() => {

        // ttl() returns (now - exp), so a still-valid token has ttl <= 0.
        const loggedIn = sessionContext.valid() &&
            sessionContext.ttl() <= 0 &&
            location.pathname !== "/logout";

        let c: any = undefined;

        if (client) client.end(true);
        setClient(undefined);
        setReceived(INITIAL_RECEIVED);

        if (loggedIn) {

            try {

                c = mqtt.connect(`wss://${process.env.REACT_APP_MQTT_ENDPOINT}/mqtt`, {
                    username: `${MQTT_USERNAME}?x-amz-customauthorizer-name=${MQTT_AUTHORIZER_NAME}`,
                    password: sessionContext.getAccessToken(),
                    clientId: `web-${uuidv4()}`,
                    // AWS IoT Core only speaks MQTT 3.1.1 and MQTT 5. Pairing
                    // `protocolId: "MQTT"` (3.1.1) with `protocolVersion: 3`
                    // (3.1) is invalid and silently rejected at the MQTT
                    // layer after the WSS handshake — leaving the client
                    // stuck in a connect→close reconnect loop. 4 is the
                    // wire-level version number for 3.1.1 and matches the
                    // "MQTT" id.
                    protocolId: "MQTT",
                    protocolVersion: 4,
                    clean: true,
                });

                const globalTopic = `${TOPIC_PREFIX}/global`;
                // THE ACCOUNT USER, NOT THE USER. The IoT policy grants this topic keyed on the
                // JWT's `sub`, which authenticate.py sets to the AccountUser USID. Subscribing
                // with the CUser USID instead asks for a topic the policy does not grant, and AWS
                // IoT answers an unauthorized SUBSCRIBE by CLOSING THE CONNECTION -- which looks
                // like a network problem and is really a one-word disagreement between two files.
                const userTopic = `${TOPIC_PREFIX}/user/${sessionContext.getAccountUserUSID()}`;

                c.on("connect", function () {
                    const topics = [globalTopic, userTopic];
                    c.subscribe(topics, function (err: any) {
                        if (err) {
                            console.error(err);
                        }
                    });
                });

                // AWS IoT closes the WebSocket when the access token's
                // disconnectAfterInSeconds (= the JWT's exp - now at connect
                // time) elapses. mqtt.js then fires `reconnect` and would
                // happily reopen the WSS with the (now expired) access
                // token cached in the connect options, get rejected by the
                // authorizer, close, reconnect, forever. We re-check
                // session liveness on every reconnect attempt — using the
                // sessionContext value FRESH rather than the closure-
                // captured `loggedIn` from effect mount time — and
                // force-end if the user has logged out OR the token has
                // expired. `end(true)` skips the DISCONNECT packet (the
                // socket may already be torn down) and stops the
                // auto-reconnect loop.
                c.on("reconnect", function () {
                    const stillLoggedIn =
                        sessionContext.valid() && sessionContext.ttl() <= 0;
                    if (!stillLoggedIn) {
                        c.end(true);
                    }
                });

                // Every subscribed-topic message bumps totalCount, but only
                // messages whose decoded payload carries
                // kind === MQTT_PROBE_KIND bump probeCount + the
                // last{Topic,Payload,ReceivedAt} fields. The NavBar
                // indicator only flashes on probeCount changes, so
                // day-to-day server traffic stays invisible and a real test
                // publish is unambiguous. Containers that want to react to
                // arbitrary messages can attach their own listener via
                // `client.on("message", ...)`.
                c.on("message", function (topic: string, payload: Buffer) {
                    let decoded: unknown = null;
                    const raw = payload?.toString?.() ?? "";
                    try { decoded = JSON.parse(raw); } catch { decoded = raw; }
                    const isProbe =
                        !!decoded &&
                        typeof decoded === "object" &&
                        (decoded as any).kind === MQTT_PROBE_KIND;
                    setReceived((prev) => ({
                        lastTopic: isProbe ? topic : prev.lastTopic,
                        lastPayload: isProbe ? decoded : prev.lastPayload,
                        lastReceivedAt: isProbe ? Date.now() : prev.lastReceivedAt,
                        probeCount: prev.probeCount + (isProbe ? 1 : 0),
                        totalCount: prev.totalCount + 1,
                    }));
                });

                c.on("error", function (err: any) {
                    console.error(err);
                    c.end();
                });

            } catch (e) {
                if (c) c.end(true);
                c = undefined;
            }

            setClient(c);
        }

        return () => {
            if (client) client.end(true);
            setClient(undefined);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionContext, location]);

    return {client, received};
}
